import Canvg from 'canvg';
import {find} from 'lodash';
import {IBrandConfig, IVisitedLocation, SessionStore, ICustomerProfile, ILocation, LocationStore, TBrand} from '../store';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {getGoogleMapsAPI, helpers} from 'gmap-vue';
const {googleMapsApiInitializer} = helpers;

export interface ILocationMarker {
	locationId: string;
	location: ILocation;
	position: google.maps.LatLng;
	icon: google.maps.Icon;
	iconHighlight: google.maps.Icon;
	iconNoOrdering: google.maps.Icon;
}

class Marker {
	private franchisorLogo: string | null = null;
	private googleMapsLoaded = false;
	private markerIconCache: Record<string, string> = {};

	loadGoogleMaps() {
		if(this.googleMapsLoaded) {
			return;
		}
		this.googleMapsLoaded = true;
		googleMapsApiInitializer({
			key: SessionStore.brandConfig?.googleMapsApiKey,
			libraries: 'places,geometry',
			// OR: libraries: 'places,drawing'
			// OR: libraries: 'places,drawing,visualization'
			// (as you require)

			// If you want to set the version, you can do so:
			// v: '3.26',
		}, false);
	}

	async getFranchisorLogo() {
		if(!this.franchisorLogo) {
			this.franchisorLogo = await this.getBase64Image(SessionStore.brandConfig ? SessionStore.brandConfig.logoImage : '/placeholder-logo.png');
		}
		return this.franchisorLogo;
	}

	get visitedMarkerColor() {
		return SessionStore.brandConfig ? SessionStore.brandConfig.singleHighlightColor : '#F44D21';
	}

	get highlightedMarkerColor() {
		return SessionStore.brandConfig ? SessionStore.brandConfig.actionPrimaryColor : '#003da5';
	}

	get noOrderingMarkerColor() {
		return '#A9A6A5';
	}

	async marker(logo: string, color: string) {
		const key = `${logo}-${color}`;
		if(!this.markerIconCache[key]) {
			const canvas = document.createElement('canvas');
			const context = canvas.getContext('2d');
			canvas.width = 36;
			canvas.height = 58;
			if(context) {
				context.clearRect(0, 0, canvas.width, canvas.height);
				const v = Canvg.fromString(context, `
				<svg width="36" style="font: 8pt sans-serif; fill: white;" height="58" viewBox="0 0 36 58" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M36 29.8368C36 48.3455 21.0529 58 18 58C14.9471 58 0 48.3532 0 29.8368C0 19.9858 8.05888 12 18 12C27.9411 12 36 19.9858 36 29.8368Z" fill="${color}"/>
					<circle cx="18" cy="30" r="13" fill="white"/>
					<image href="${logo}" x="5" y="17" height="25" width="25"/>
				</svg>
			`);
				await v.render();
			}
			this.markerIconCache[key] = canvas.toDataURL('image/png');
		}
		return this.markerIconCache[key];
	}

	async markerWithPill(logo: string, width: number, color: string, text: string, pillWidth: number) {
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		canvas.width = width;
		canvas.height = 58;
		if(context) {
			context.clearRect(0, 0, canvas.width, canvas.height);
			const v = Canvg.fromString(context, `
				<svg width="${width}" style="font: 8pt sans-serif; fill: white;" height="58" viewBox="-19 0 74 58" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M36 29.8368C36 48.3455 21.0529 58 18 58C14.9471 58 0 48.3532 0 29.8368C0 19.9858 8.05888 12 18 12C27.9411 12 36 19.9858 36 29.8368Z" fill="${color}"/>
					<circle cx="18" cy="30" r="13" fill="white"/>
					<g>
						<rect x="18.5" y="0.5" width="${pillWidth}" height="17" rx="8.5" fill="${color}" stroke="white"/>
						<text x="24" y="13">${text}</text>
					</g>
					<image href="${logo}" x="5" y="17" height="25" width="25"/>
				</svg>
			`);
			await v.render();
		}
		return canvas.toDataURL('image/png');
	}

	get google() {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return getGoogleMapsAPI();
	}

	async getVisitedLocationMarker(visitedLocation: IVisitedLocation, location: ILocation): Promise<ILocationMarker> {
		const markerHeight = 58;
		let markerWidth: number;
		let pillWidth: number;
		let pillText: string;
		if(visitedLocation.visits === 1) {
			pillText = `${visitedLocation.visits} Visit`;
			pillWidth = 41;
			markerWidth = 86;
		} else if(visitedLocation.visits < 10) {
			pillText = `${visitedLocation.visits} Visits`;
			pillWidth = 45;
			markerWidth = 92;
		} else if(visitedLocation.visits < 100) {
			pillText = `${visitedLocation.visits} Visits`;
			pillWidth = 50;
			markerWidth = 102;
		} else {
			pillText = `${visitedLocation.visits} Visits`;
			pillWidth = 55;
			markerWidth = 112;
		}

		return {
			locationId: visitedLocation.id,
			location: location,
			position: new this.google.maps.LatLng({
				lat: visitedLocation.latitude,
				lng: visitedLocation.longitude
			}),
			icon: {
				url: await this.markerWithPill(
					await this.getFranchisorLogo(),
					markerWidth,
					this.visitedMarkerColor,
					pillText,
					pillWidth
				),
				size: new this.google.maps.Size(markerWidth, markerHeight),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(markerWidth / 2, markerHeight)
			},
			iconHighlight: {
				url: await this.markerWithPill(
					await this.getFranchisorLogo(),
					markerWidth,
					this.highlightedMarkerColor,
					pillText,
					pillWidth
				),
				size: new this.google.maps.Size(markerWidth, markerHeight),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(markerWidth / 2, markerHeight)
			},
			iconNoOrdering: {
				url: await this.markerWithPill(
					await this.getFranchisorLogo(),
					markerWidth,
					this.noOrderingMarkerColor,
					pillText,
					pillWidth
				),
				size: new this.google.maps.Size(markerWidth, markerHeight),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(markerWidth / 2, markerHeight)
			},
		};
	}

	async getLocationMarker(location: ILocation): Promise<ILocationMarker> {
		return {
			locationId: location.id,
			location: location,
			position: new this.google.maps.LatLng({
				lat: location.latitude,
				lng: location.longitude
			}),
			icon: {
				url: await this.marker(
					await this.getFranchisorLogo(),
					'#000'
				),
				size: new this.google.maps.Size(36, 58),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(36 / 2, 58)
			},
			iconHighlight: {
				url: await this.marker(
					await this.getFranchisorLogo(),
					this.highlightedMarkerColor,
				),
				size: new this.google.maps.Size(36, 58),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(36 / 2, 58)
			},
			iconNoOrdering: {
				url: await this.marker(
					await this.getFranchisorLogo(),
					this.noOrderingMarkerColor,
				),
				size: new this.google.maps.Size(36, 58),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(36 / 2, 58)
			},
		};
	}

	getBrandMarkerForLocation(location: ILocation, brand: TBrand): ILocationMarker {
		return {
			locationId: location.id,
			location: location,
			position: new this.google.maps.LatLng({
				lat: location.latitude,
				lng: location.longitude
			}),
			icon: {
				url: require(`../assets/icons/map-pins/${brand}.svg`),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(36 / 2, 58)
			},
			iconHighlight: {
				url: require(`../assets/icons/map-pins/${brand}-highlight.svg`),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(36 / 2, 58)
			},
			iconNoOrdering: {
				url: require(`../assets/icons/map-pins/${brand}-no-ordering.svg`),
				origin: new this.google.maps.Point(0, 0),
				anchor: new this.google.maps.Point(36 / 2, 58)
			}
		};
	}

	async getLocationMarkers(customerProfile: ICustomerProfile, brandConfig: IBrandConfig) {
		const locationMarkers: ILocationMarker[] = [];
		if(customerProfile && brandConfig) {
			for(const location of LocationStore.locationsSortedByDistance) {
				const visitedLocation = find(customerProfile.visitedLocations, {
					id: location.id
				}) as IVisitedLocation || null;
				if(visitedLocation) {
					locationMarkers.push(await this.getVisitedLocationMarker(visitedLocation, location));
				} else {
					locationMarkers.push(await this.getLocationMarker(location));
				}
			}
		}
		return locationMarkers;
	}

	getStaticLocationMarkers(customerProfile: ICustomerProfile, brandConfig: IBrandConfig, brand: TBrand) {
		const locationMarkers: ILocationMarker[] = [];
		if(customerProfile && brandConfig) {
			for(const location of LocationStore.locationsSortedByDistance) {
				locationMarkers.push(this.getBrandMarkerForLocation(location, brand));
			}
		}
		return locationMarkers;
	}

	async getBase64Image(imagePath: string): Promise<string> {
		return new Promise((resolve, reject) => {
			const image = document.createElement('img');
			image.crossOrigin = 'Anonymous';
			image.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = image.width;
				canvas.height = image.height;
				const ctx = canvas.getContext('2d');
				if(ctx) {
					ctx.drawImage(image, 0, 0);
					const dataURL = canvas.toDataURL('image/png');
					resolve(dataURL);
				}
				reject(new Error('No context'));
			};
			image.onerror = (error) => {
				reject(error);
			};
			image.src = imagePath;
		});
	}
}


export const MarkerService = new Marker();
